'use client';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Link from 'next/link';
import { ArrowRight, Package, BookOpen } from 'lucide-react';
import { NPM_PACKAGE_URL } from '@/lib/site';

const codeStyle = {
  margin: 0,
  padding: '1rem',
  background: 'rgba(0, 0, 0, 0.5)',
  fontSize: '0.8rem',
};

export default function DocsSection() {
  return (
    <section id="docs" className="sm:py-24 pt-16 pb-16 relative">
      <div className="sm:px-6 lg:px-8 max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-sm font-medium text-rose-400">Documentation</span>
          <h2 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Install in under 2 minutes
          </h2>
          <p className="mt-4 text-base sm:text-lg text-neutral-300">
            Pick one of three ways to deploy - same engine, same dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="rounded-none border p-6 border-white/10 bg-neutral-900/70" data-scroll-animate="fade-up">
            <h3 className="text-xl font-medium mb-1">Node.js SDK <span className="text-xs text-rose-400 align-middle ml-2 px-2 py-0.5 rounded-none bg-rose-500/10 border border-rose-500/20">Recommended</span></h3>
            <p className="text-sm text-neutral-300 mb-4 mt-2">
              One line in any Node app - Express, Next.js, Nest. Inbound + outbound.
            </p>
            <div className="rounded-none overflow-hidden border border-neutral-800/50">
              <SyntaxHighlighter language="typescript" style={vscDarkPlus} customStyle={codeStyle}>
{`// Express / Node - zero-config (reads SILKER_API_KEY from env)
import { middleware } from '@silker-ai/agent'
app.use(middleware())

// Next.js (middleware.ts) - Edge-ready
import { nextMiddleware } from '@silker-ai/agent/next'
export const config = { matcher: '/api/:path*' }
export default nextMiddleware()`}
              </SyntaxHighlighter>
            </div>
          </div>

          <div className="rounded-none border p-6 border-white/10 bg-neutral-900/70" data-scroll-animate="fade-up">
            <h3 className="text-xl font-medium mb-1">Cloudflare Worker <span className="text-xs text-neutral-400 align-middle ml-2 px-2 py-0.5 rounded-none bg-white/5 border border-white/10">Coming soon</span></h3>
            <p className="text-sm text-neutral-300 mb-4 mt-2">
              Any backend behind Cloudflare. Runs on your zone.
            </p>
            <div className="rounded-none overflow-hidden border border-neutral-800/50">
              <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={codeStyle}>
{`# set SILKER_APP_ID + SILKER_TARGET in wrangler.toml
wrangler secret put SILKER_API_KEY
wrangler deploy

# Done - traffic is inspected at the edge.`}
              </SyntaxHighlighter>
            </div>
          </div>
        </div>

        <div className="rounded-none border p-6 border-white/10 bg-neutral-900/70 mb-8" data-scroll-animate="fade-up">
          <h3 className="text-xl font-medium mb-1">Docker Container <span className="text-xs text-neutral-400 align-middle ml-2 px-2 py-0.5 rounded-none bg-white/5 border border-white/10">Self-host · any stack</span></h3>
          <p className="text-sm text-neutral-300 mb-4 mt-2">
            Run the proxy in front of any app - PHP, Java, Python, Go. No Cloudflare required.
          </p>
          <div className="rounded-none overflow-hidden border border-neutral-800/50">
            <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={codeStyle}>
{`docker run -d \\
  -p 8080:8080 \\
  -e SILKER_TARGET=http://your-app:3000 \\
  -e SILKER_API_KEY=sk_your_key \\
  -e SILKER_APP_ID=your_app_id \\
  ghcr.io/niceappspl/silker-proxy

# Point your traffic to :8080 - that's it.`}
            </SyntaxHighlighter>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center" data-scroll-animate="fade-up">
          <a
            href={NPM_PACKAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-none font-medium hover:bg-white/10 transition-colors border border-white/10"
          >
            <Package className="w-4 h-4" />
            @silker-ai/agent on npm
          </a>
          <Link
            href="/docs"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-none font-medium hover:bg-white/10 transition-colors border border-white/10"
          >
            <BookOpen className="w-4 h-4" />
            Full documentation
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
